<template>
  <div class="d-flex">
    <v-tooltip top color="primary">
      <template v-slot:activator="{ on }">
        <v-btn
          v-on="on"
          @click="createPDF(false)"
          color="primary"
          :disabled="disabled"
          class="mr-1"
        >
          <font-awesome-icon icon="file-pdf" size="lg" />
        </v-btn>
      </template>
      <span>{{ tooltip }}</span>
    </v-tooltip>
    <v-tooltip top color="primary">
      <template v-slot:activator="{ on }">
        <v-btn
          v-on="on"
          @click="createPDF(true)"
          color="primary"
          :disabled="disabled"
        >
          <font-awesome-icon icon="download" size="lg" />
        </v-btn>
      </template>
      <span>Download</span>
    </v-tooltip>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import moment, { min } from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import probe from "probe-image-size";

export default {
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    timesheet: Object,
    invoice: Object,
    title: String,
  },
  data() {
    return {
      headers: [
        {
          day: "Day",
          date: "Date",
          description: "Description",
          location: "Location",
          info: "Info",
          start: "Start",
          finish: "Finish",
          hours: "Total Hours",
          breaks: "Breaks",
          workHours: "Working Hours",
          basic: "Basic",
          overtime: "Overtime",
        },
      ],
      invoiceHeaders: [
        {
          candidate: "Candidate",
          role: "Description",
          site: "Location",
          info: "Info",
          hours: "Hours",
          wage: "Wage/Hr",
          amount: "Amount",
        },
      ],
    };
  },
  computed: {
    ...mapState({
      account: (state) => state.account,
      clients: (state) => state.clients,
    }),
    tooltip() {
      return this.title == null ? "Preview" : this.title;
    },
    timesheetClients() {
      var clients = [
        ...new Set(
          this.timesheet.bookings
            .filter((b) => b.client._id)
            .map((b) => b.client)
        ),
      ];
      if (clients.length == 0) {
        clients.push({});
      }
      return clients;
    },
    client() {
      return this.clients.items.find((c) => c._id == this.invoice.client._id);
    },
    items() {
      return this.invoice.items;
    },

    company() {
      return this.account.company;
    },

    location() {
      return this.account.location;
    },
  },
  methods: {
    ...mapActions({
      getURL: "documents/getURL",
    }),
    createPDF(save) {
      if (this.timesheet) {
        if (save) {
          this.createTimesheetSave(this.timesheet);
        } else {
          this.createTimesheet(this.timesheet);
        }
      } else if (this.invoice) {
        this.createInvoice(this.invoice, save);
      }
    },

    async createInvoice(invoice, save) {
      var doc = new jsPDF("p", "mm", "a4");

      await this.addInvoice(doc, 0);

      doc.setProperties({
        title: "Invoice",
      });

      if (save) {
        doc.save(`Invoice ${invoice.invoiceNo} ${invoice.client.name}`);
      } else {
        doc.output("dataurlnewwindow");
      }
    },

    async createTimesheet(timesheet) {
      var doc = new jsPDF("l", "mm", "a4");

      var i = 0;
      for (var client of this.timesheetClients) {
        await this.addTimesheetForClient(client, doc, i);
        i++;
      }

      doc.setProperties({
        title: "Timesheet",
      });

      doc.output("dataurlnewwindow");
    },

    async createTimesheetSave(timesheet) {
      for (var client of this.timesheetClients) {
        var doc = new jsPDF("l", "mm", "a4");
        await this.addTimesheetForClient(client, doc, 0);

        doc.setProperties({
          title: "Timesheet",
        });

        doc.save(
          `${moment(this.timesheet.weekCommencing).format("DD-MMM-YY")} ${
            timesheet.candidate.name
          } ${client.name}`
        );
      }
    },

    async addInvoice(doc, i) {
      var lineWidth = 0.4;
      var lineColor = 220;

      console.log(this.invoice);

      if (i > 0) {
        doc.addPage();
      }

      var pageWidth = parseInt(doc.internal.pageSize.getWidth());
      var pageHeight = parseInt(doc.internal.pageSize.getHeight());

      await this.addImage(doc, pageWidth);

      var y = 40;
      var x = 15;
      doc.setFontSize(30);
      doc.text(x, y + 5, "INVOICE");

      doc.setFontSize(10);
      if (this.client && this.client.address) {
        doc.text(x, y + 15, this.client.name);
        doc.text(x, y + 20, this.client.address.address || "");
        doc.text(x, y + 25, this.client.address.city || "");
        doc.text(x, y + 30, this.client.address.postcode || "");
      }

      x = pageWidth - 110;
      doc.setFont(undefined, "bold");
      doc.text(x, y, "Invoice Date");
      doc.setFont(undefined, "normal");
      console.log(this.invoice.date);

      doc.text(x, y + 5, moment(this.invoice.date).format("DD-MMM-YY"));

      doc.setFont(undefined, "bold");
      doc.text(x, y + 12.5, "Invoice #");
      doc.setFont(undefined, "normal");
      doc.text(x, y + 17.5, `${this.invoice.invoiceNo}`);

      if (this.invoice.purchaseOrderNo) {
        doc.setFont(undefined, "bold");
        doc.text(x + 25, y + 12.5, "PO #");
        doc.setFont(undefined, "normal");
        doc.text(x + 25, y + 17.5, `${this.invoice.purchaseOrderNo}`);
      }

      doc.setFont(undefined, "bold");
      doc.text(x, y + 25, "Week Period");
      doc.setFont(undefined, "normal");
      doc.text(
        x,
        y + 30,
        `${moment(this.invoice.start).format("DD-MMM-YY")}  -  ${moment(
          this.invoice.finish
        ).format("DD-MMM-YY")}`
      );

      x = pageWidth - 15;
      doc.text(x, y, this.company.name, { align: "right" });
      doc.text(x, y + 5, this.location.address, { align: "right" });
      doc.text(x, y + 10, this.location.city, { align: "right" });
      doc.text(x, y + 15, this.location.postcode, { align: "right" });

      doc.setFont(undefined, "bold");
      doc.text(x, y + 25, "VAT Number", { align: "right" });
      doc.setFont(undefined, "normal");
      doc.text(x, y + 30, "190967761", { align: "right" });

      doc.autoTable({
        startY: y + 40,
        theme: "grid",
        head: this.invoiceHeaders,
        body: this.items,
        margin: 0,
        showHead: "firstPage",

        styles: {
          textColor: 20,
          lineColor: 0,
          lineWidth: 0,
          halign: "center",
          valign: "middle",
          minCellHeight: 9,
        },
        bodyStyles: {
          fontSize: 9,
        },
        columnStyles: {
          candidate: { halign: "left" },
          amount: { cellWidth: 30 },
        },
        headStyles: {
          fillColor: 255,
          fontSize: 10,
        },
        didDrawCell: (data) => {
          if (data.section == "head") {
            doc.setDrawColor(lineColor);
            doc.setLineWidth(0.5);
            doc.line(
              data.cell.x,
              data.cell.y + data.cell.height,
              data.cell.x + data.cell.width,
              data.cell.y + data.cell.height
            );
          } else if (data.section == "body") {
            doc.setDrawColor(lineColor);
            doc.setLineWidth(0.5);

            doc.line(
              data.cell.x,
              data.cell.y + data.cell.height,
              data.cell.x + data.cell.width,
              data.cell.y + data.cell.height
            );
          }
        },
      });

      doc.autoTable({
        startY: doc.previousAutoTable.finalY,
        theme: "grid",
        body: [
          [
            {
              content: "",
              colSpan: 5,
              styles: { halign: "center", fillColor: [255] },
            },
            {
              content: "Subtotal",
              colSpan: 1,
              styles: {
                halign: "right",
                fillColor: [255],
                cellPadding: { right: 3 },
              },
            },
            {
              content: `£${this.invoice.subtotal.toFixed(2)}`,
              colSpan: 1,
              styles: { halign: "center", fillColor: [245], fontStyle: "bold" },
            },
          ],
          [
            {
              content: "",
              colSpan: 5,
              styles: { halign: "left", fillColor: [255] },
            },
            {
              content: "VAT Rate",
              colSpan: 1,
              styles: {
                halign: "right",
                fillColor: [255],
                cellPadding: { right: 3 },
              },
            },
            {
              content: "20%",
              colSpan: 1,
              styles: { halign: "center", fillColor: [245], fontStyle: "bold" },
            },
          ],
          [
            {
              content: "",
              colSpan: 5,
              styles: {
                halign: "center",
                fillColor: [255],
              },
            },
            {
              content: "Sales Tax",
              colSpan: 1,
              styles: {
                halign: "right",
                fillColor: [2555],
                cellPadding: { right: 3 },
              },
            },
            {
              content: `£${this.invoice.vat.toFixed(2)}`,
              colSpan: 1,
              styles: { halign: "center", fillColor: [245], fontStyle: "bold" },
            },
          ],
          [
            {
              content: "",
              colSpan: 5,
              styles: { halign: "Left", fillColor: [255] },
            },
            {
              content: "Total",
              colSpan: 1,
              styles: {
                halign: "right",
                fillColor: [255],
                cellPadding: { right: 3 },
              },
            },
            {
              content: `£${(this.invoice.subtotal + this.invoice.vat).toFixed(
                2
              )}`,
              colSpan: 1,
              styles: { halign: "center", fillColor: [245], fontStyle: "bold" },
            },
          ],
        ],

        showHead: "never",
        head: this.invoiceHeaders,
        margin: 0,

        styles: {
          textColor: 20,
          lineColor: 0,
          lineWidth: 0,
          halign: "center",
          valign: "middle",
          minCellHeight: 8,
        },
        bodyStyles: {
          fontSize: 9,
        },
        columnStyles: {
          amount: { cellWidth: 30 },
        },
      });

      y = doc.previousAutoTable.finalY + 5;

      var diff = pageHeight - (y + 10);
      if (diff < 38) {
        doc.addPage();
        y = 20;
      }

      doc.setDrawColor(150);
      doc.setLineDash([3, 3], 0);
      doc.line(15, y - 5, pageWidth - 14, y - 5);

      console.log(this.client);
      var terms = 28;
      if (this.client) {
        terms = this.client.paymentTerms;
      }
      var dueDate = moment(this.invoice.date).add(terms, "days");
      doc.setFontSize(14);
      doc.setFont(undefined, "bold");
      doc.text(15, y, `Due Date: ${dueDate.format("DD-MMM-YY")}`);

      doc.setFontSize(10);
      doc.text(65, y, `(${terms} days)`);

      doc.setFont(undefined, "normal");

      doc.setFontSize(12);
      doc.text(15, y + 8, "Bank Details");

      doc.setFontSize(10);
      doc.text(15, y + 12, "Account Name: SureStaffing UK Ltd");
      doc.text(15, y + 16, "Account Number: 10633355");
      doc.text(15, y + 20, "Sort Code: 09-02-22");

      doc.setDrawColor(100);
      doc.setLineDash([3, 3], 0);
      // doc.line(15, y + 26, pageWidth - 15, y + 26);

      var text =
        "All invoice disputes must be reported within 3 working days and failure to do so does not absolve your obligation to pay the charges in respect of the hours worked";

      doc.setFont(undefined, "normal");
      doc.setFontSize(9);
      var lines = doc.splitTextToSize(text, pageWidth - 30);
      doc.text(15, y + 26, lines);

      this.addFooter(doc, pageWidth, pageHeight);
    },
    addFooter(doc, pageWidth, pageHeight) {
      var y = pageHeight - 9;
      var x = 12;
      doc.setFont(undefined, "normal");
      doc.setFontSize(8);
      doc.text(
        pageWidth / 2,
        y,
        "Company Registration No: 08798304. Registered Office: 27-29 Hessle Road, Hull, East Yorkshire, HU3 2AA.",
        { align: "center" }
      );
    },
    invoices(client) {
      var arr = this.timesheet.bookings
        .filter((b) => b.invoice && b.show && b.client._id == client._id)
        .map((b) => b.invoice.invoiceNo);

      return [...new Set(arr)];
    },

    days(client) {
      return this.timesheet.bookings.map((b) => {
        if (!b.show || b.client._id != client._id) {
          return {
            day: b.day,
            date: b.date.format("DD-MMM-YYYY"),
          };
        } else {
          return {
            day: b.day,
            date: b.date.format("DD-MMM-YYYY"),
            description: b.role,
            location: b.site,
            info: b.info,
            start: b.start,
            finish: b.finish,
            hours: b.hours,
            breaks: b.breaks,
            workHours: b.workHours,
            basic: b.basic,
            overtime: b.overtime,
          };
        }
      });
    },
    expenses(client) {
      var expenses = this.timesheet.expenses
        .filter((e) => e.client._id == client._id)
        .map((e) => {
          return {
            type: e.type,
            quantity: e.quantity,
            amount: `£${e.amount.toFixed(2)}`,
          };
        });
      if (expenses.length == 0) {
        expenses.push({});
      }
      return expenses;
    },

    totalHoursForClient(client) {
      return this.timesheet.bookings
        .filter((b) => {
          return b.client._id == client._id;
        })
        .reduce((a, b) => +(parseFloat(a) || 0) + (parseFloat(b.hours) || 0), 0)
        .toFixed(2);
    },

    totalBreaksForClient(client) {
      return this.timesheet.bookings
        .filter((b) => {
          return b.client._id == client._id;
        })
        .reduce(
          (a, b) =>
            +(parseFloat(a) || 0) +
            (parseFloat(
              parseFloat(b.breaks.substring(0, 2)) +
                parseFloat(b.breaks.substring(3, 5) / 60)
            ) || 0),
          0
        )
        .toFixed(2);
    },

    totalWorkHoursForClient(client) {
      return this.timesheet.bookings
        .filter((b) => {
          return b.client._id == client._id;
        })
        .reduce(
          (a, b) => +(parseFloat(a) || 0) + (parseFloat(b.workHours) || 0),
          0
        )
        .toFixed(2);
    },

    totalBasicHoursForClient(client) {
      return this.timesheet.bookings
        .filter((b) => {
          return b.client._id == client._id;
        })
        .reduce((a, b) => +(parseFloat(a) || 0) + (parseFloat(b.basic) || 0), 0)
        .toFixed(2);
    },

    totalOvertimeHoursForClient(client) {
      return this.timesheet.bookings
        .filter((b) => {
          return b.client._id == client._id;
        })
        .reduce(
          (a, b) => +(parseFloat(a) || 0) + (parseFloat(b.overtime) || 0),
          0
        )
        .toFixed(2);
    },

    async addTimesheetForClient(client, doc, i) {
      var lineWidth = 0.4;
      var lineColor = 150;

      if (i > 0) {
        doc.addPage();
      }

      var pageWidth = parseInt(doc.internal.pageSize.getWidth());
      var pageHeight = parseInt(doc.internal.pageSize.getHeight());

      var date = moment(this.timesheet.weekCommencing);

      doc.autoTable({
        startY: 10,
        theme: "grid",

        body: [
          {
            key: "Name",
            val: this.timesheet.candidate.name,
          },
          {
            key: "Invoice No",
            val: this.invoices(client).join(", "),
          },
          {
            key: "Week Commencing",
            val: moment(this.timesheet.weekCommencing).format("DD-MMM-YY"),
          },
          {
            key: "Client",
            val: client.name,
          },
        ],
        styles: {
          textColor: 20,
          lineColor: 0,
          lineWidth: 0,
          valign: "middle",
        },
        bodyStyles: {
          fontSize: 12,
          cellPadding: { top: 0.5, right: 2, bottom: 0.5, left: 2 },
        },
        columnStyles: {
          key: { cellWidth: 44, cellHeight: 5, halign: "right" },
          val: { halign: "left" },
        },
        didDrawCell: (data) => {
          if (data.column.index > 0) {
            doc.setDrawColor(lineColor);
            doc.setLineWidth(lineWidth);
            doc.line(
              data.cell.x,
              data.cell.y,
              data.cell.x,
              data.cell.y + data.cell.height
            );
          }
        },
      });

      doc.autoTable({
        startY: doc.previousAutoTable.finalY + 5,
        theme: "grid",
        head: this.headers,
        body: this.days(client),
        foot: [
          [
            {
              content: "",
              colSpan: 6,
              styles: { halign: "center", fillColor: [255] },
            },
            {
              content: "Total",
              colSpan: 1,
              styles: { halign: "center", fillColor: [210] },
            },
            {
              content: this.totalHoursForClient(client),
              colSpan: 1,
              styles: { halign: "center", fillColor: [245] },
            },
            {
              content: this.totalBreaksForClient(client),
              colSpan: 1,
              styles: { halign: "center", fillColor: [245] },
            },
            {
              content: this.totalWorkHoursForClient(client),
              colSpan: 1,
              styles: { halign: "center", fillColor: [245] },
            },
            {
              content: this.totalBasicHoursForClient(client),
              colSpan: 1,
              styles: { halign: "center", fillColor: [210] },
            },
            {
              content: this.totalOvertimeHoursForClient(client),
              colSpan: 1,
              styles: { halign: "center", fillColor: [210] },
            },
          ],
        ],
        headStyles: {
          fillColor: 255,
          fontSize: 10,
        },

        styles: {
          textColor: 20,
          lineColor: lineColor,
          lineWidth: lineWidth,
          halign: "center",
          valign: "middle",
          minCellHeight: 10,
        },
        bodyStyles: {
          fontSize: 9,
        },
        columnStyles: {
          day: { cellWidth: 22 },
          date: { cellWidth: 22 },
          start: { cellWidth: 15 },
          finish: { cellWidth: 15 },
          hours: { cellWidth: 15 },
          breaks: { cellWidth: 15 },
          workHours: { cellWidth: 18 },
          basic: { cellWidth: 15 },
          overtime: { cellWidth: 20 },
        },
      });

      var y = doc.previousAutoTable.finalY + 5;

      var body = [];
      var expenses = this.expenses(client);
      for (var i = 0; i < expenses.length; i++) {
        var row = [];
        for (var key in expenses[i]) {
          row.push(expenses[i][key]);
        }
        body.push(row);
      }

      doc.autoTable({
        startY: y,
        theme: "grid",
        head: [
          [
            {
              content: "Expenses",
              colSpan: 3,
              styles: { halign: "center", fillColor: [255] },
            },
          ],
          [
            {
              content: "Type",
              colSpan: 1,
              styles: { halign: "center", fillColor: [255], cellWidth: 44 },
            },
            {
              content: "Quantity",
              colSpan: 1,
              styles: { halign: "center", fillColor: [255], cellWidth: 23 },
            },
            {
              content: "Amount",
              colSpan: 1,
              styles: { halign: "center", fillColor: [255], cellWidth: 23 },
            },
          ],
        ],
        body: body,
        tableWidth: 90,
        headStyles: {
          fillColor: 255,
          fontSize: 10,
        },
        styles: {
          textColor: 20,
          lineColor: lineColor,
          lineWidth: lineWidth,
          halign: "center",
          valign: "middle",
          cellHeight: 10,
        },
        bodyStyles: {
          fontSize: 9,
        },
      });

      doc.setFont(undefined, "bold");
      doc.setFontSize(13);
      doc.text(115, y + 7, "Client Decleration:");

      var text = `In accordance with our terms of business, a signature of the timesheet by the Hirer is confirmation of the number of hours worked. If the Hirer is unable to sign a timesheet produced for authentication by the Agency Worker because the Hirer disputes the hours claimed, the Hirer shall inform the Employment Business within 24 hours and shall co-operate fully and in a timely fashion with the Employment Business to enable the Employment Business to establish what hours, if any, were worked by the Agency Worker. Failure to sign the timesheet does not absolve the Hirer of its obligation to pay the Charges in respect of the hours worked.`;

      doc.setFont(undefined, "normal");
      doc.setFontSize(9);
      var lines = doc.splitTextToSize(text, 165);
      doc.text(115, y + 15, lines);

      doc.setLineDash([1, 1], 0);

      text = "Signed:";
      doc.text(115, y + 45, text);
      var width = doc.getTextWidth(text);
      doc.line(120 + width, y + 45, 275, y + 45);

      text = "Print:";
      doc.text(115, y + 55, text);
      width = doc.getTextWidth(text);
      doc.line(120 + width, y + 55, 275, y + 55);

      doc.setDrawColor(lineColor);
      doc.setLineWidth(lineWidth);
      doc.setLineDash(0);
      doc.rect(110, y, 173, 60);

      await this.addImage(doc, pageWidth);
    },

    async addImage(doc, pageWidth) {
      var url;
      if (this.company.logo) {
        url = await this.getURL(this.company.logo);
      }
      if (!url) {
        this.addImageText(doc, pageWidth);
        return;
      }

      console.log(url);

      var result = await this.getDataUri(url);
      if (result) {
        var imgHeight = 20;
        var imgWidth = imgHeight * result.aspectRatio;
        var x = pageWidth - imgWidth - 15;
        doc.addImage(result.data, "JPEG", x, 10, imgWidth, imgHeight);
      } else {
        this.addImageText(doc, pageWidth);
      }
    },

    getDataUri(url) {
      return new Promise((resolve, reject) => {
        var img = new Image();
        img.setAttribute("crossOrigin", "anonymous"); //getting images from external domain
        img.onload = function () {
          var canvas = document.createElement("canvas");
          canvas.width = this.naturalWidth;
          canvas.height = this.naturalHeight;
          //next three lines for white background in case png has a transparent background
          var ctx = canvas.getContext("2d");
          ctx.fillStyle = "#fff"; /// set white fill style
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          canvas.getContext("2d").drawImage(this, 0, 0);
          var data = canvas.toDataURL("image/jpeg");
          var aspectRatio = img.width / img.height;
          resolve({ data, aspectRatio });
        };
        img.onerror = reject;
        img.src = url;
      });
    },

    addImageText(doc, pageWidth) {
      var name = this.company.name;
      var maxWidth = pageWidth / 2;
      // name = "Really long company name here";
      doc.setFontSize(30);
      var width = doc.getTextWidth(name);
      var x = pageWidth - 15;
      var y = 20;
      if (width > maxWidth) {
        y = 15;
      }
      doc.text(name, x, y, {
        align: "right",
        maxWidth: maxWidth,
        baseline: "middle",
      });
    },
  },
};
</script>

<style></style>
